import {StyleSheet, View, Dimensions, FlatList} from 'react-native';
import Styles from '../../Styles';
import AVText from 'src/components/elements/AVText';
import Localized from 'src/constants/AppStrings';
import RecentLocationView, {
  RecentLocationType,
} from 'src/components/elements/home/RecentLocationView';
import {IsConnectedProps} from 'src/types/Screen';
import Settings from 'src/Settings';
import {getDescriber} from './descriptor/DescriptorType';
import {useAppSelector} from 'src/redux/hooks';
import {deleteRecentCampusLocation} from 'src/redux/slices/campusLocationSlice';
import AccountStore from 'src/stores/AccountStore';
import {store} from 'src/redux/store';
import ErrorModal from 'src/components/elements/ErrorModal';
import {useState} from 'react';
import {getHeaderDescriber} from 'src/components/elements/headerDescriptor/DescriptorType';

type RecentLocationsListProps = IsConnectedProps & {
  mismatchLocationBalance: (value: boolean) => void;
};

const {getCampusLocationStyles} = getDescriber();
const width = Dimensions.get('window').width;

const RecentLocationsList = (props: RecentLocationsListProps) => {
  const locations = useAppSelector(
    (s) => s.campusLocation.recentCampusesAndLocations,
  );

  const [isLocationWarningVisible, setIsLocationWarningVisible] =
    useState(false);

  const deleteLocation = (locationId: string) => {
    const defaultLocationId = AccountStore.getLocationId();
    if (defaultLocationId !== locationId) {
      store.dispatch(
        deleteRecentCampusLocation({
          accountId: AccountStore.getAccountId(),
          locationId: locationId,
        }),
      );
    } else {
      showLocationWarning();
    }
  };

  const closeLocationWarning = () => {
    setIsLocationWarningVisible(false);
  };

  const showLocationWarning = () => {
    setIsLocationWarningVisible(true);
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: RecentLocationType;
    index: number;
  }) => {
    const onDeleteLocation = () => deleteLocation(item.locationId);
    const isShowBorder = index !== locations.length - 1;
    return (
      <RecentLocationView
        recentLocation={item}
        onDelete={onDeleteLocation}
        showBorder={isShowBorder}
        mismatchLocationBalance={props.mismatchLocationBalance}
      />
    );
  };

  if (locations.length <= 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerView}>
        <AVText
          style={[
            getCampusLocationStyles().descriptionText,
            styles.headerText,
            getHeaderDescriber().subHeaderContainerStyleDescriptor()
              .subHeaderTextStyle,
          ]}
          accessible={true}
          accessibilityLabel={Localized.Labels.recents}
          accessibilityRole="text"
          aria-label={`${Localized.Labels.recents}, text`}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
        >
          {Localized.Labels.recents}
        </AVText>
      </View>
      <FlatList
        contentContainerStyle={[
          getCampusLocationStyles().locationListView,
          Settings.isRefiveAnd365Pay() && styles.tileDesign,
          getCampusLocationStyles().recentListborder,
        ]}
        data={locations}
        keyExtractor={(item, index) => {
          return index + (item.campusId ?? item.locationId) + item.name;
        }}
        scrollEnabled={false}
        renderItem={renderItem}
        ItemSeparatorComponent={() => (
          <View style={getCampusLocationStyles().separator} />
        )}
      />
      <ErrorModal
        isModalVisible={isLocationWarningVisible}
        modalContentStyle={styles.modalErrorContent}
        headerTitle={Localized.Labels.home_location_needed}
        description={Localized.Errors.home_location_cannot_deleted}
        button1Title={Localized.Buttons.ok_got_it}
        button1TextStyle={styles.errorButton}
        button2Hide={true}
        button1Action={closeLocationWarning}
        onModalClose={closeLocationWarning}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  tileDesign: {
    borderColor: Styles.white,
    borderRadius: Styles.Spacing.m2,
    ...Styles.Style.shadow,
  },
  separator: {
    width: 288,
    height: 1,
    backgroundColor: '#CFCFCF',
    marginLeft: 50,
  },
  headerView: {
    width: width,
  },
  headerText: {
    color: Styles.black,
    marginHorizontal: Styles.Spacing.m3,
    fontFamily: Settings.isCanteen() ? 'Arial Black' : undefined,
  },
  swipeableContainer: {
    overflow: 'hidden',
  },
  swipeableChildren: {
    flex: 1,
    backgroundColor: Settings.isRefiveAnd365Pay()
      ? Styles.Colors.PayNew.white01
      : Styles.Colors.Canteen.lightGray,
  },
  errorButton: {
    textTransform: 'none',
    width: 100,
  },
  modalErrorContent: {
    width: 350,
  },
});
export default RecentLocationsList;
